import React from 'react';

import { Button } from '@/design-system/button';
import { Input, InputRoot } from '@/design-system/input';
import { Popover, PopoverTrigger } from '@/design-system/popover';
import { FilterPopoverContent } from '@/components/FilterPopover';
import { SearchSelect } from '@/design-system/search-select';
import { useSearchableOptions } from '@/hooks/useSearchableOptions';
import { searchAllPitstopOptions } from '@/services/api/pitstops';

// Same server-side provider dropdown the Shipments filter uses — one hook,
// one behavior (page-1 on open, debounced ?search=, id→name label cache).
import type { DatePreset } from '../../shipments/components/ShipmentFilterPanel';
import { useProvidersDropdown } from '../../shipments/components/useProvidersDropdown';

export interface DispatchFilterValue {
  /** Numeric hub/pitstop id, stringified. */
  hubId: string;
  /** Numeric provider ids, stringified (multi-select — OR semantics). */
  clientIds: string[];
  /** Preset chip — mutually exclusive with the manual from/to pair. */
  datePreset: DatePreset | '';
  /** Manual `YYYY-MM-DD` bounds on created_at. */
  from: string;
  to: string;
}

export const EMPTY_DISPATCH_FILTERS: DispatchFilterValue = {
  hubId: '',
  clientIds: [],
  datePreset: '',
  from: '',
  to: '',
};

/** Date counts as ONE active filter whether preset or manual range. */
export const countActiveDispatchFilters = (v: DispatchFilterValue): number =>
  (v.hubId ? 1 : 0) +
  (v.clientIds.length > 0 ? 1 : 0) +
  (v.datePreset || v.from || v.to ? 1 : 0);

const DATE_PRESETS: { key: DatePreset; label: string }[] = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'last_7_days', label: 'Last 7 days' },
  { key: 'last_30_days', label: 'Last 30 days' },
];

interface DispatchFilterPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Number of APPLIED filters — the ink pill on the trigger. */
  activeFilterCount: number;
  /** The draft being edited (parent seeds it from the URL on open). */
  value: DispatchFilterValue;
  onChange: (next: DispatchFilterValue) => void;
  onApply: () => void;
  onReset: () => void;
}

/**
 * Filter popover of the Dispatches list — the same shape and behavior as the
 * Shipments filter (draft-in-popover, applied-in-URL, server-side searches).
 * Fields: date (preset or manual range on created_at), hub, client.
 */
const DispatchFilterPanel: React.FC<DispatchFilterPanelProps> = ({
  open,
  onOpenChange,
  activeFilterCount,
  value,
  onChange,
  onApply,
  onReset,
}) => {
  const labelCls = 'mb-2 block text-[11px] font-medium text-text-sub-600';

  // Hub list: ALL pitstops — Dash hubs and client pitstops alike, since a
  // dispatch can depart from either. The opening fetch (empty ?search=)
  // seeds the list; typing searches SERVER-SIDE so the whole masterdata
  // stays reachable, not just the first page.
  const hubs = useSearchableOptions(open, searchAllPitstopOptions);

  const providers = useProvidersDropdown(open);

  const updateDatePreset = (preset: DatePreset) => {
    onChange({
      ...value,
      datePreset: value.datePreset === preset ? '' : preset,
      from: '',
      to: '',
    });
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button tone="neutral" style="stroke" size="sm">
          <i className="ri-equalizer-2-line mr-1.5 text-[14px]" />
          Filter
          {activeFilterCount > 0 && (
            <span className="ml-1.5 rounded-md bg-bg-strong-950 px-1.5 py-0.5 text-[10px] font-bold tabular-nums text-text-white-0">
              {activeFilterCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <FilterPopoverContent
        align="end"
        className="w-[360px] p-0"
        aria-label="Filter dispatches"
      >
        <div className="flex items-center justify-between border-b border-stroke-soft-200 px-4 py-3">
          <p className="text-sm font-semibold tracking-tight text-text-strong-950">
            Filter dispatches
          </p>
          <button
            type="button"
            onClick={onReset}
            className="text-[11px] font-medium text-text-sub-600 transition-colors hover:text-accent"
          >
            Reset
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto space-y-4 px-4 py-3">
          <div>
            <p className={labelCls}>Date</p>
            <div className="flex flex-wrap gap-1">
              {DATE_PRESETS.map((p) => {
                const active = value.datePreset === p.key;
                return (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => updateDatePreset(p.key)}
                    className={`rounded-lg border px-2.5 py-1 text-xs font-semibold tracking-tight transition-colors ${
                      active
                        ? 'border-accent text-accent'
                        : 'border-stroke-soft-200 text-text-sub-600 hover:bg-bg-weak-50'
                    }`}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>
            <div className="mt-2 flex items-center gap-2">
              <InputRoot size="sm" className="flex-1">
                <Input
                  type="date"
                  value={value.from}
                  onChange={(e) =>
                    onChange({ ...value, from: e.target.value, datePreset: '' })
                  }
                />
              </InputRoot>
              <span className="text-xs text-text-soft-400">to</span>
              <InputRoot size="sm" className="flex-1">
                <Input
                  type="date"
                  value={value.to}
                  onChange={(e) =>
                    onChange({ ...value, to: e.target.value, datePreset: '' })
                  }
                />
              </InputRoot>
            </div>
          </div>

          <div>
            <p className={labelCls}>Hub</p>
            <SearchSelect
              options={hubs.options.map((h) => ({
                value: String(h.id),
                label: h.code ? `${h.code} · ${h.name}` : h.name,
              }))}
              value={value.hubId || undefined}
              selectedLabelFallback={
                value.hubId ? hubs.labelFor(value.hubId) : undefined
              }
              onChange={(v) =>
                onChange({ ...value, hubId: (v as string | undefined) ?? '' })
              }
              onSearchChange={hubs.setSearch}
              disableLocalFilter
              loading={hubs.loading}
              placeholder="All hubs"
              allowClear
              emptyMessage={hubs.error ?? 'No matching hub'}
            />
          </div>

          <div>
            <p className={labelCls}>Client</p>
            <SearchSelect
              multiple
              // Selected ids ride along as options even when a narrowing
              // search drops them from the results — otherwise their chips
              // degrade to the raw id. labelFor is the hook's id→name cache.
              options={[
                ...value.clientIds
                  .filter(
                    (id) => !providers.options.some((p) => String(p.id) === id)
                  )
                  .map((id) => ({
                    value: id,
                    label: providers.labelFor(id) ?? id,
                  })),
                ...providers.options.map((p) => ({
                  value: String(p.id),
                  label: p.name,
                })),
              ]}
              value={value.clientIds}
              onChange={(v) =>
                onChange({ ...value, clientIds: (v ?? []).map(String) })
              }
              onSearchChange={providers.setSearch}
              disableLocalFilter
              loading={providers.loading}
              hasMore={providers.hasMore}
              onLoadMore={providers.loadMore}
              placeholder="All clients"
              allowClear
              emptyMessage={providers.error ?? 'No matching client'}
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-stroke-soft-200 px-4 py-3">
          <Button
            tone="neutral"
            style="stroke"
            size="sm"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button size="sm" onClick={onApply}>
            Apply
          </Button>
        </div>
      </FilterPopoverContent>
    </Popover>
  );
};

export default DispatchFilterPanel;
